import React from 'react';

interface ToggleOption {
  code: string;
  label: string;
}

interface Column {
  label: string;
  column: string;
  type: 'static' | 'dynamic';
  toggle?: ToggleOption[];
  description?: string;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  renderCell?: (value: any, row: any, column: Column) => React.ReactNode;
  pinned?: 'left' | 'right' | false;
}

interface DataTableCellProps {
  column: Column;
  row: any;
  isLastColumn: boolean;
  currentToggleIndex?: number;
  rowIndex: number;
  columnIndex: number;
  value?: any; // Pre-calculated value
  renderedCell?: React.ReactNode; // Pre-rendered cell content
  onClick?: () => void;
  className?: string;
  pinned?: 'left' | 'right' | false;
}

/**
 * DataTableCell - A table cell component for displaying data
 *
 * @description A table cell that displays data based on column configuration,
 * supporting both static and dynamic data with column pinning and full accessibility compliance.
 *
 * @example
 * ```tsx
 * <DataTableCell
 *   column={column}
 *   row={rowData}
 *   isLastColumn={false}
 *   currentToggleIndex={0}
 *   rowIndex={0}
 *   columnIndex={0}
 *   value="Cell Value"
 *   onClick={() => console.log('Cell clicked')}
 *   pinned="left"
 * />
 * ```
 */
const DataTableCell: React.FC<DataTableCellProps> = ({
  column,
  row,
  currentToggleIndex = 0,
  rowIndex,
  columnIndex,
  value,
  renderedCell,
  onClick,
  className,
  pinned,
}) => {
  let cellValue = value;

  // If no pre-calculated value, calculate it
  if (cellValue === undefined) {
    if (column.type === 'static') {
      cellValue = row[column.column] ?? '';
    } else if (column.type === 'dynamic') {
      const currentToggle = column.toggle?.[currentToggleIndex];
      const dataKey = currentToggle?.code;
      cellValue = row[column.column]?.[dataKey] ?? '';
    }
  }

  // Convert value to string if it's not already
  const displayValue =
    cellValue !== null && cellValue !== undefined ? cellValue.toString() : '';

  // Determine alignment
  const alignment = column.align || 'left';
  const textAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[alignment];

  // Add pinned-specific styling
  const pinnedClasses =
    pinned === 'left'
      ? 'shadow-[2px_0_4px_rgba(0,0,0,0.1)]'
      : pinned === 'right'
      ? 'shadow-[-2px_0_4px_rgba(0,0,0,0.1)]'
      : '';

  return (
    <div
      className={`px-3 py-3 text-sm text-primary border-b border-neutral-200 hover:bg-neutral-50 ${textAlignClass} ${
        onClick ? 'cursor-pointer' : ''
      } ${pinnedClasses} ${className || ''}`}
      role="cell"
      aria-colindex={columnIndex + 1}
      data-testid={`cell-${rowIndex}-${columnIndex}`}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {renderedCell || displayValue}
    </div>
  );
};

export default DataTableCell;
